/**
 * Safe API fetch utility that avoids DataCloneError.
 * Uses XMLHttpRequest as the transport to bypass any fetch() wrappers
 * (service workers, platform scripts) that may try to clone Request objects.
 * 
 * Uses RELATIVE URLs (/api/...) to guarantee same-origin requests,
 * eliminating all CORS issues. Falls back to REACT_APP_BACKEND_URL
 * only if explicitly needed.
 */

const getBaseUrl = () => {
  // In production: use relative URLs (same-origin, no CORS issues)
  // In development: use REACT_APP_BACKEND_URL if set
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '';  // Relative URL - always same-origin
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

export const apiPost = (endpoint, body) => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      const base = getBaseUrl();
      xhr.open('POST', `${base}${endpoint}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      xhr.timeout = 30000;
      xhr.onload = () => {
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || `Request failed (${xhr.status})`));
          }
        } catch (parseErr) {
          reject(new Error(`Invalid response from server (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));
      xhr.ontimeout = () => reject(new Error('Request timed out. Please try again.'));
      xhr.send(JSON.stringify(body));
    } catch (e) {
      reject(new Error('Failed to make request: ' + e.message));
    }
  });
};

export const apiGet = (endpoint) => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      const base = getBaseUrl();
      xhr.open('GET', `${base}${endpoint}`);
      xhr.withCredentials = true;
      xhr.timeout = 30000;
      xhr.onload = () => {
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || `Request failed (${xhr.status})`));
          }
        } catch (parseErr) {
          reject(new Error(`Invalid response from server (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));
      xhr.ontimeout = () => reject(new Error('Request timed out. Please try again.'));
      xhr.send();
    } catch (e) {
      reject(new Error('Failed to make request: ' + e.message));
    }
  });
};

export const apiUpload = (endpoint, formData) => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      const base = getBaseUrl();
      xhr.open('POST', `${base}${endpoint}`);
      xhr.withCredentials = true;
      xhr.timeout = 60000;
      xhr.onload = () => {
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || `Upload failed (${xhr.status})`));
          }
        } catch (parseErr) {
          reject(new Error(`Invalid response from server (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));
      xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again.'));
      xhr.send(formData);
    } catch (e) {
      reject(new Error('Failed to upload: ' + e.message));
    }
  });
};
